import React, { useRef, useEffect } from 'react';
import { useMultipleSelection, useSelect } from '@indeed/downshift';
import isFunction from '../../utils/isFunction.js';
import isEqual from 'react-fast-compare';
import { FORM_SIZES } from '../../constants/index.js';
import useId from '@indeed/ihl-use-id';
import warnDeprecation from '../../utils/warnDeprecation.js';
import { jsx } from '@emotion/react';
import '../../utils/warn.js';

const DropdownContext = /*#__PURE__*/React.createContext({});
/**
 * @see https://experience.indeed.design/components/ids/dropdown
 *
 * @deprecated The `Dropdown` component will be removed in IFL 7.
 * See the IFL 7 preparation guide for more information: https://link.indeed.tech/ifl-7-prep-guide
 */

const Dropdown = _ref => {
  let {
    children,
    disabled = false,
    invalid = false,
    multiple = false,
    onChange,
    onClose,
    onOpen,
    useMultipleSelectionProps = {},
    useSelectProps = {}
  } = _ref;
  warnDeprecation(deprecationWarning);
  const prevMultiple = useRef(multiple);
  const latest = useRef({
    items: []
  });
  const isMobileRef = useRef(false);
  const targetRef = useRef(null);
  const multipleSelection = useMultipleSelection({
    onSelectedItemsChange: _ref2 => {
      let {
        selectedItems
      } = _ref2;
      if (onChange && selectedItems &&
      // IFL-1580: Don't call onChange if controlled value is the same as selected value
      !isEqual(selectedItems, useMultipleSelectionProps.selectedItems)) {
        onChange(selectedItems, context);
      }
    },
    ...useMultipleSelectionProps
  });
  const generatedId = useId();
  const select = useSelect({
    /**
     * Remove the default live region behavior when opened. From the below
     * GitHub issue:
     *
     * "Screen reader users can usually turn off the help/hint text for their
     * screen reader in order to reduce verbosity, and since they wouldn't be
     * able to turn this off, it would get annoying pretty quickly."
     *
     * See below for more information.
     * @see https://github.com/downshift-js/downshift/issues/1244
     * @see https://github.com/carbon-design-system/carbon/pull/7777#issuecomment-783863657
     *
     * Unfortunately, this does not actually remove the element the live region
     * is attached to.
     */
    getA11yStatusMessage: () => "",
    getA11ySelectionMessage: () => "",
    items: latest.current.items,
    stateReducer: (state, actionAndChanges) => {
      const {
        changes,
        type
      } = actionAndChanges;
      const newChanges = {
        ...changes
      };
      switch (type) {
        case useSelect.stateChangeTypes.MenuKeyDownEnter:
        case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          if (multiple && changes.selectedItem && multipleSelection.selectedItems.indexOf(changes.selectedItem) >= 0) {
            multipleSelection.removeSelectedItem(changes.selectedItem);
            newChanges.selectedItem = null;
          }
          return {
            ...newChanges,
            highlightedIndex: state.highlightedIndex,
            isOpen: !!multiple
          };
        case useSelect.stateChangeTypes.MenuBlur:
          /**
           * Prevent menu from closing when "mobile" menu blurs.
           * See DropdownMenuList.tsx for more information.
           */
          if (isMobileRef.current) {
            return {
              ...newChanges,
              isOpen: true
            };
          }
          break;
        case useSelect.stateChangeTypes.FunctionReset:
          // Prevent toggling open state on reset, as DropdownMenuItem calls this for each item.
          return {
            ...newChanges,
            isOpen: state.isOpen
          };
      }
      return changes;
    },
    onSelectedItemChange: _ref3 => {
      let {
        selectedItem
      } = _ref3;
      if (selectedItem) {
        if (multiple) {
          multipleSelection.addSelectedItem(selectedItem);
        } else {
          multipleSelection.setSelectedItems([selectedItem]);
        }
      }
    },
    onIsOpenChange: _ref4 => {
      let {
        isOpen
      } = _ref4;
      if (onClose && !isOpen) {
        onClose();
      }
      if (onOpen && isOpen) {
        onOpen();
      }
      if (!isOpen && targetRef.current) {
        targetRef.current.focus();
      }
    },
    id: `downshift-${generatedId}`,
    ...useSelectProps
  });
  const toggleButtonProps = select.getToggleButtonProps({
    ...multipleSelection.getDropdownProps({
      preventKeyAction: select.isOpen
    }),
    disabled,
    onKeyDown: e => {
      // Allow Space and Enter to open menu
      if ([" ", "Enter"].indexOf(e.key) >= 0) {
        /**
         * Downshift is already listening for "space" and "enter" keys.
         * Preventing default to avoid opening and immediately closing the menu.
         */
        e.preventDefault();
        select.openMenu();
      }
    }
  });
  const context = {
    disabled,
    invalid,
    items: latest.current.items,
    latest,
    multiple,
    isMobile: isMobileRef.current,
    setIsMobile: isMobile => isMobileRef.current = isMobile,
    targetRef,
    toggleButtonProps,
    ...multipleSelection,
    ...select
  };
  useEffect(() => {
    if (!multiple) {
      if (useMultipleSelectionProps.selectedItems && useMultipleSelectionProps.selectedItems.length > 0 && select.selectedItem && select.selectedItem !== useMultipleSelectionProps.selectedItems[0]) {
        select.selectItem(useMultipleSelectionProps.selectedItems[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useMultipleSelectionProps.selectedItems]);
  useEffect(() => {
    if (prevMultiple.current !== multiple) {
      const [firstItem] = multipleSelection.selectedItems;
      multipleSelection.setSelectedItems(firstItem ? [firstItem] : []);
      prevMultiple.current = multiple;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiple]);
  return jsx(DropdownContext.Provider, {
    value: context
  }, isFunction(children) ? children(context) : children);
};
const DROPDOWN_SIZES = FORM_SIZES;
Dropdown.displayName = "Dropdown";
const deprecationWarning = `[Dropdown] The Dropdown component will be removed in IFL 7. See the IFL 7 preparation guide for more information: https://link.indeed.tech/ifl-7-prep-guide`;

export { DROPDOWN_SIZES, Dropdown, DropdownContext };
